import {inject, Injectable} from '@angular/core';
import {SatelliteProductService} from '@6grain/masofa-monolith-api-client/common';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {NotReadyApiService} from '@app/api/not-ready-api-service';
import {FilterOperator} from '@app/utils/filter-operator';
import {ymdFromApiDate, ymdToLocalDate} from './field-details-date.util';

/** Fallback index key when satellite product list is empty (same season reports as timeline source). */
export const INDEX_KEY_FOR_TIMELINE_FALLBACK = 'Ndvi';

/** Cloud cover above this percent → grey “cloud cover > 50%” dot on the timeline. */
export const CLOUD_COVER_TIMELINE_THRESHOLD = 50;

/** Normalizes API cloudCover to 0–100 (handles 0–1 fractions and 0–100 percent). */
export function cloudCoverToPercent(raw: unknown): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return null;
  }
  return n <= 1 ? n * 100 : n;
}

@Injectable({providedIn: 'root'})
export class FieldDetailsSatelliteTimelineService {
  private readonly satelliteProductService = inject(SatelliteProductService);
  private readonly notReadyApi = inject(NotReadyApiService);

  /** Uses generated client {@link SatelliteProductService.commonSatelliteProductGetAllProductsByPointGet}. */
  fetchProductsByPoint(lat: number, lng: number): Observable<any[]> {
    return this.satelliteProductService.commonSatelliteProductGetAllProductsByPointGet(lat, lng).pipe(
      catchError(() => of([] as any[])),
      map(rows => (Array.isArray(rows) ? rows : [])),
    );
  }

  /** Drops products strictly before planting day when `plantingYmd` is set. */
  applyPlantingFilter(products: any[], plantingYmd: string | null): any[] {
    if (!plantingYmd) {
      return products;
    }
    const startMs = new Date(plantingYmd).setHours(0, 0, 0, 0);
    return products.filter(r => {
      if (!r?.originDate) {
        return true;
      }
      return new Date(r.originDate).setHours(0, 0, 0, 0) >= startMs;
    });
  }

  /** `YYYY-MM-DD` keys where product `cloudCover` exceeds {@link CLOUD_COVER_TIMELINE_THRESHOLD}. */
  highCloudCoverDaysFromProducts(
    products: any[],
    threshold = CLOUD_COVER_TIMELINE_THRESHOLD,
  ): Set<string> {
    const out = new Set<string>();
    for (const r of products) {
      if (!r?.originDate) {
        continue;
      }
      const pct = cloudCoverToPercent(r.cloudCover ?? r.CloudCover);
      if (pct == null || pct <= threshold) {
        continue;
      }
      const ymd = ymdFromApiDate(r.originDate);
      if (ymd) {
        out.add(ymd);
      }
    }
    return out;
  }

  /**
   * One {@link Date} per calendar day from product `originDate`, sorted ascending.
   *
   * Dates are normalized to local midnight of the stored calendar day so that the day key
   * (`dateToYmd`) used when matching anomalies/cloud-cover stays consistent regardless
   * of the acquisition time-of-day / timezone.
   */
  timelineDatesFromProducts(products: any[]): Date[] {
    const byDay = new Map<string, Date>();
    for (const r of products) {
      if (!r?.originDate) {
        continue;
      }
      const ymd = ymdFromApiDate(r.originDate);
      if (ymd) {
        byDay.set(ymd, ymdToLocalDate(ymd));
      }
    }
    return [...byDay.values()].sort((a, b) => a.getTime() - b.getTime());
  }

  /** When satellite API returns no usable dates: derive days from index season reports (Ndvi). */
  loadFallbackTimelineDates$(seasonId: string, plantingYmd: string | null): Observable<Date[]> {
    const seasonFilter = {
      filterField: 'seasonId',
      filterOperator: FilterOperator.Equals,
      filterValue: seasonId,
    };
    return this.notReadyApi
      .GetIndexSeasonReports({filters: [seasonFilter]}, INDEX_KEY_FOR_TIMELINE_FALLBACK)
      .pipe(
        catchError(() => of([] as any[])),
        map((rows: any[]) => {
          const byDay = new Map<string, Date>();
          for (const r of rows || []) {
            const ymd = ymdFromApiDate(r?.dateOnly);
            if (!ymd) {
              continue;
            }
            if (plantingYmd && ymd < plantingYmd) {
              continue;
            }
            byDay.set(ymd, ymdToLocalDate(ymd));
          }
          return [...byDay.values()].sort((a, b) => a.getTime() - b.getTime());
        }),
      );
  }
}
